import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config';
import { getToken, getUser } from '../auth';
import '../styles/SupportChat.css';

function SupportChat() {
  const navigate = useNavigate();
  const user = getUser();
  const token = getToken();

  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState(null);
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [newTicket, setNewTicket] = useState({ subject: '', message: '' });
  const [creatingTicket, setCreatingTicket] = useState(false);

  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const selectedTicketRef = useRef(null);

  const showNotice = (type, text) => {
    setNotice({ type, text });
    setTimeout(() => setNotice(null), 2600);
  };

  const fetchTickets = useCallback(async (showLoader = true) => {
    if (showLoader) setLoadingTickets(true);
    try {
      const res = await fetch(`${API_BASE_URL}/support/my-tickets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          navigate(`/${user.role}/signin`);
          return;
        }
        return;
      }
      const data = await res.json();
      setTickets(data.tickets || []);
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
    } finally {
      if (showLoader) setLoadingTickets(false);
    }
  }, [token, user.role, navigate]);

  const fetchTicketMessages = useCallback(async (ticketId, showLoader = true) => {
    if (showLoader) setLoadingMsgs(true);
    try {
      const res = await fetch(`${API_BASE_URL}/support/ticket/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
        setSelectedTicket((prev) =>
          prev?.id === data.id ? { ...prev, status: data.status } : prev
        );
      }
    } catch (err) {
      console.error('Failed to fetch ticket messages:', err);
    } finally {
      if (showLoader) setLoadingMsgs(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token || !['hirer', 'technician'].includes(user.role)) {
      navigate('/');
      return;
    }
    fetchTickets();
    pollRef.current = setInterval(() => {
      fetchTickets(false);
      if (selectedTicketRef.current) fetchTicketMessages(selectedTicketRef.current.id, false);
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, []);

  useEffect(() => {
    selectedTicketRef.current = selectedTicket;
  }, [selectedTicket]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const selectTicket = (ticket) => {
    setSelectedTicket(ticket);
    fetchTicketMessages(ticket.id);
    setMobileShowChat(true);
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!reply.trim() || !selectedTicket || sending) return;

    const content = reply.trim();
    setReply('');
    setSending(true);

    const optimistic = {
      id: `temp_${Date.now()}`,
      sender_type: 'user',
      content,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);

    try {
      const res = await fetch(`${API_BASE_URL}/support/ticket/${selectedTicket.id}/reply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
        setReply(content);
        showNotice('error', errData.detail || 'Failed to send. Please try again.');
        return;
      }

      await fetchTicketMessages(selectedTicket.id, false);
      await fetchTickets(false);
    } catch {
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      setReply(content);
      showNotice('error', 'Network error. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const createTicket = async (e) => {
    e.preventDefault();
    if (!newTicket.subject.trim() || !newTicket.message.trim()) return;
    setCreatingTicket(true);

    try {
      const res = await fetch(`${API_BASE_URL}/support/ticket`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          subject: newTicket.subject.trim(),
          message: newTicket.message.trim(),
        }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        showNotice('error', errData.detail || 'Failed to create ticket.');
        return;
      }

      const created = await res.json();
      setNewTicket({ subject: '', message: '' });
      setShowNewTicket(false);
      showNotice('success', "Ticket created! We'll respond as soon as possible.");

      await fetchTickets();
      const newT = {
        id: created.id,
        subject: newTicket.subject.trim(),
        status: 'open',
        message_count: 1,
        created_at: new Date().toISOString(),
      };
      setSelectedTicket(newT);
      await fetchTicketMessages(created.id);
      setMobileShowChat(true);
    } catch {
      showNotice('error', 'Network error. Please try again.');
    } finally {
      setCreatingTicket(false);
    }
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const diff = Date.now() - d.getTime();
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="sup-page">
      {notice && (
        <div className={`sup-notice sup-notice--${notice.type}`} role="status">
          {notice.text}
        </div>
      )}

      {/* ── Sidebar ── */}
      <div className={`sup-sidebar${mobileShowChat ? ' sup-sidebar--hidden' : ''}`}>
        <div className="sup-sidebar-head">
          <div className="sup-sidebar-head-top">
            <h2 className="sup-title">Support</h2>
            <button className="sup-back-btn" onClick={() => navigate(`/${user.role}/dashboard`)}>
              ← Back
            </button>
          </div>
          <button className="sup-new-btn" onClick={() => setShowNewTicket(true)}>
            + New Ticket
          </button>
        </div>

        {loadingTickets ? (
          <div className="sup-state">
            <div className="sup-spinner" />
            <p>Loading…</p>
          </div>
        ) : tickets.length === 0 ? (
          <div className="sup-state">
            <span className="sup-state-icon">🎧</span>
            <p>No tickets yet</p>
            <p className="sup-state-hint">Click "+ New Ticket" to contact support.</p>
          </div>
        ) : (
          <div className="sup-ticket-list">
            {tickets.map((t) => (
              <button
                key={t.id}
                className={`sup-ticket-item${selectedTicket?.id === t.id ? ' active' : ''}`}
                onClick={() => selectTicket(t)}
              >
                <div className="sup-ticket-top">
                  <span className="sup-ticket-subject">{t.subject}</span>
                  <span className={`sup-status-badge sup-status-badge--${t.status}`}>
                    {t.status}
                  </span>
                </div>
                <div className="sup-ticket-meta">
                  <span>{t.message_count} message{t.message_count !== 1 ? 's' : ''}</span>
                  <span>{formatTime(t.created_at)}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Chat panel ── */}
      <div className={`sup-chat${!mobileShowChat && !selectedTicket ? ' sup-chat--hidden' : ''}`}>
        {!selectedTicket ? (
          <div className="sup-no-selection">
            <span className="sup-no-sel-icon">🎧</span>
            <h3>Select a ticket</h3>
            <p>Or create a new one to contact support.</p>
          </div>
        ) : (
          <>
            <div className="sup-chat-header">
              <button className="sup-mobile-back" onClick={() => setMobileShowChat(false)}>←</button>
              <div className="sup-support-avatar">S</div>
              <div className="sup-chat-meta">
                <h3 className="sup-chat-subject">{selectedTicket.subject}</h3>
                <span className={`sup-status-badge sup-status-badge--${selectedTicket.status}`}>
                  {selectedTicket.status}
                </span>
              </div>
            </div>

            <div className="sup-chat-body">
              {loadingMsgs ? (
                <div className="sup-msgs-loading"><div className="sup-spinner" /></div>
              ) : messages.length === 0 ? (
                <div className="sup-msgs-empty">No messages yet.</div>
              ) : (
                messages.map((msg) => (
                  <div key={msg.id} className={`sup-bubble${msg.sender_type === 'user' ? ' sent' : ' received'}`}>
                    <div className="sup-bubble-label">
                      {msg.sender_type === 'user' ? 'You' : 'SkillArea Support'}
                    </div>
                    <div className="sup-bubble-text">{msg.content}</div>
                    <div className="sup-bubble-time">{formatTime(msg.created_at)}</div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {selectedTicket.status === 'open' ? (
              <form className="sup-input-row" onSubmit={sendReply}>
                <input
                  className="sup-input"
                  type="text"
                  placeholder="Type your message…"
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  disabled={sending}
                  autoComplete="off"
                />
                <button
                  type="submit"
                  className="sup-send-btn"
                  disabled={!reply.trim() || sending}
                  aria-label="Send reply"
                >
                  {sending ? (
                    <span className="sup-spinner sup-spinner--sm" />
                  ) : (
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
                      <path d="M22 2L11 13" stroke="white" strokeWidth="2" strokeLinecap="round" />
                      <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>
              </form>
            ) : (
              <div className="sup-closed-bar">
                This ticket is closed. Create a new ticket if you need further help.
              </div>
            )}
          </>
        )}
      </div>

      {/* ── New Ticket Modal ── */}
      {showNewTicket && (
        <div className="sup-modal-overlay" onClick={() => setShowNewTicket(false)}>
          <div className="sup-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sup-modal-head">
              <h3>New Support Ticket</h3>
              <button className="sup-modal-close" onClick={() => setShowNewTicket(false)} aria-label="Close">✕</button>
            </div>
            <form onSubmit={createTicket} className="sup-modal-form">
              <label className="sup-field">
                <span>Subject <span className="sup-req">*</span></span>
                <input
                  type="text"
                  className="sup-field-input"
                  placeholder="What do you need help with?"
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket((p) => ({ ...p, subject: e.target.value }))}
                  maxLength={200}
                  required
                />
              </label>
              <label className="sup-field">
                <span>Message <span className="sup-req">*</span></span>
                <textarea
                  className="sup-field-input sup-field-textarea"
                  placeholder="Describe your issue in detail…"
                  value={newTicket.message}
                  onChange={(e) => setNewTicket((p) => ({ ...p, message: e.target.value }))}
                  rows={5}
                  maxLength={2000}
                  required
                />
              </label>
              <div className="sup-modal-actions">
                <button
                  type="button"
                  className="sup-btn-cancel"
                  onClick={() => setShowNewTicket(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="sup-btn-submit"
                  disabled={!newTicket.subject.trim() || !newTicket.message.trim() || creatingTicket}
                >
                  {creatingTicket ? 'Submitting…' : 'Submit Ticket'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default SupportChat;
